import React from "react";
import { Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import CustomIcon from "../../../../../../../public/icons-v1_1";
import PathPickBasketOverseasAddressCard from "@/features/pathpick/features/basket/components/buyer-info/PathPickBasketOverseasAddressCard";
import PathPickInlineNotice from "@/features/pathpick/components/ui/PathPickInlineNotice";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useGetNaverPartnerAddressBooks from "@/features/pathpick/features/basket/hooks/query/useGetNaverPartnerAddressBooks";
import {
  findOverseasReleaseAddress,
  formatLastCheckedAt,
  isJustCheckedAt,
} from "@/features/pathpick/features/basket/utils";

// 스마트스토어 주소록에 패스픽 해외 출고지가 등록됐는지 확인하는 카드.
// 판매자 기본 정보 설정 모달의 "해외 출고지 설정" 섹션 안에 들어간다(카드 헤더는 섹션 셸이 그린다).
//
// 등록이 확인되면 옮겨 적을 일이 없으므로 주소 카드를 감추고 일치 메시지만 남긴다.
//
// 등록 여부는 네이버 주소록 조회로 판정하는데, 그 주소록은 판매자ID가 연동된 뒤에야 읽을 수 있다 —
// 미연동이면 판정 자체가 불가능하므로 선행 안내만 남긴다.
function PathPickBasketOverseasReleaseField() {
  const { t } = useI18nHelper();
  // 연결 상태를 따로 조회하지 않고 주소록 훅의 판정을 그대로 쓴다 —
  // 노출 조건과 주소록 조회 조건이 어긋나지 않아야 한다.
  const {
    addressBooks,
    isNaverPartnerConnected,
    isNaverPartnerStatusUnknown,
    isError: isAddressBooksError,
    isFetching,
    dataUpdatedAt,
    refetch,
  } = useGetNaverPartnerAddressBooks();

  const isVerified = Boolean(findOverseasReleaseAddress(addressBooks));
  // 새로고침이 실패해도 앞서 받아둔 주소록이 있으면 그 판정을 유지한다 —
  // 보여줄 게 아무것도 없을 때만 조회 실패로 전환한다(실패 사실은 토스트가 알린다).
  const isAddressBooksUnavailable =
    isAddressBooksError && addressBooks === undefined;

  // 한 번도 조회에 성공한 적이 없으면(조회 실패 포함) dataUpdatedAt이 0이라 1970년이 찍힌다.
  const lastCheckedLabel = dataUpdatedAt
    ? t("path_pick:basket.overseas_release.last_checked", {
        datetime: isJustCheckedAt(dataUpdatedAt)
          ? t("path_pick:basket.overseas_release.just_now")
          : formatLastCheckedAt(dataUpdatedAt),
      })
    : null;

  // 연결 상태를 못 읽으면 연동 여부부터 알 수 없다 — 출고지 등록 여부를 단정하지 않는다.
  // (미연동으로 안내하면 이미 연동한 사용자를 스마트스토어 관리자로 헛걸음시킨다)
  if (isNaverPartnerStatusUnknown) {
    return (
      <PathPickInlineNotice
        tone={"red"}
        action={
          <AppButton
            variant={"white"}
            size={"small"}
            isLoading={isFetching}
            onClick={() => refetch()}
          >
            <Text textStyle={"body-sm-bold"}>
              {t("path_pick:basket.overseas_release.refresh_status")}
            </Text>
          </AppButton>
        }
      >
        {t("path_pick:basket.overseas_release.status_unavailable")}
      </PathPickInlineNotice>
    );
  }

  // 연동 전에는 주소를 옮겨 적어도 확인할 방법이 없어, 옮겨 적을 주소와 상태 배너는 감추고
  // 무엇을 먼저 해야 하는지만 알린다.
  // 확인 버튼은 자리를 지키되(연동 후 어디를 눌러야 하는지 미리 보인다) 눌러도 읽을 주소록이
  // 없으므로 비활성으로 두고, 왜 못 누르는지를 버튼 바로 밑에서 밝힌다.
  if (!isNaverPartnerConnected) {
    return (
      <Flex flexDirection={"column"} gap={"12px"}>
        <PathPickInlineNotice tone={"yellow"}>
          {t("path_pick:basket.buyer_info.overseas_release.requires_api_sync")}
        </PathPickInlineNotice>
        <Flex flexDirection={"column"} gap={"4px"} alignItems={"flex-start"}>
          <AppButton variant={"white"} isDisabled={true}>
            <Flex gap={"4px"} alignItems={"center"}>
              <CustomIcon src={"arrow-rotate"} width={16} height={16} />
              <Text textStyle={"body-sm-bold"}>
                {t("path_pick:basket.overseas_release.refresh_status")}
              </Text>
            </Flex>
          </AppButton>
          <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
            {t("path_pick:basket.overseas_release.requires_api_sync_hint")}
          </Text>
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex flexDirection={"column"} gap={"12px"}>
      {/* 주소록을 못 읽었을 때 "출고지 미등록"으로 안내하면 등록해 둔 사용자를 헛걸음시킨다 —
          판정 대신 조회 실패임을 밝히고 다시 확인하게 한다. */}
      {isAddressBooksUnavailable ? (
        <PathPickInlineNotice tone={"red"}>
          {t("path_pick:basket.overseas_release.load_error")}
        </PathPickInlineNotice>
      ) : isVerified ? (
        <Flex
          gap={"6px"}
          alignItems={"center"}
          padding={"12px"}
          borderRadius={"8px"}
          border={"0.5px solid #A9E0AB"}
          background={"#F0FAEF"}
        >
          <CustomIcon src={"check"} width={16} height={16} color={"#4C994F"} />
          <Text textStyle={"body-sm-medium"} color={"#4C994F"}>
            {t("path_pick:basket.overseas_release.matched")}
          </Text>
        </Flex>
      ) : (
        <>
          <PathPickBasketOverseasAddressCard />
          <PathPickInlineNotice tone={"red"}>
            {t("path_pick:basket.overseas_release.error")}
          </PathPickInlineNotice>
        </>
      )}

      <Flex justifyContent={"space-between"} alignItems={"center"} gap={"8px"}>
        <AppButton
          variant={"white"}
          isDisabled={isFetching}
          onClick={() => refetch()}
        >
          <Flex gap={"4px"} alignItems={"center"}>
            <CustomIcon src={"arrow-rotate"} width={16} height={16} />
            <Text textStyle={"body-sm-bold"}>
              {t("path_pick:basket.overseas_release.refresh_status")}
            </Text>
          </Flex>
        </AppButton>
        {lastCheckedLabel && (
          <Text textStyle={"caption-medium"} color={"#8F8F8C"}>
            {lastCheckedLabel}
          </Text>
        )}
      </Flex>
    </Flex>
  );
}

export default PathPickBasketOverseasReleaseField;
